import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPersonSearch } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import GymIdMemberSearch from '../../../components/receptionist/GymIdMemberSearch';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import { useAuth } from '../../../context/AuthContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import { getGymMemberId } from '../members/memberData';
import {
  exportMemberCsv,
  exportMemberExcel,
  exportMemberPdf,
  exportMembers,
  printMemberList,
} from '../members/exportMembers';
import { MEMBER_SEARCH_PLACEHOLDER } from '../../receptionist/sales/salePageConfig';
import { fetchMembersExport, fetchMembersPage } from '../../../services/receptionist/receptionistApi';
import { setActiveGymId } from '../../../services/receptionist/gymContext';
import { dedupeMembers } from '../../../utils/dedupeList';
import TablePagination from '../../../components/shared/TablePagination';
import '../../owner/ownerPages.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './allModule.css';

function memberMobile(member) {
  return String(member?.mobile || member?.phone || '').trim();
}

function MemberCard({ member, onClick }) {
  return (
    <button
      type="button"
      className="admin-customer-ledger-card"
      onClick={() => onClick(member)}
    >
      <span className="admin-customer-ledger-card-name">{member.name || 'Unnamed member'}</span>
      <span className="admin-customer-ledger-card-line">{getGymMemberId(member) || '—'}</span>
      <span className="admin-customer-ledger-card-line">{memberMobile(member) || '—'}</span>
    </button>
  );
}

export default function AllModule() {
  const navigate = useNavigate();
  const toast = usePanelToast();
  const { account } = useAuth();
  const gymId = account?.gym_id || '';

  const [members, setMembers] = useState([]);
  const [memberHits, setMemberHits] = useState([]);
  const [query, setQuery] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 48;
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const dateFilters = {
    rangeStart,
    rangeEnd,
    dateFrom: rangeStart,
    dateTo: rangeEnd,
  };

  const loadMembers = useCallback(async () => {
    if (!gymId) {
      setLoadingMembers(false);
      setError('No gym is linked to this account.');
      return;
    }
    try {
      setLoadingMembers(true);
      setError('');
      setActiveGymId(gymId);
      // List stays on get_members_page (admin all-time — no PK-today lock).
      const result = await fetchMembersPage({
        page,
        pageSize,
        search: query.trim(),
        filters: dateFilters,
      });
      setMembers(dedupeMembers(result.rows || []));
      setTotal(Number(result.total) || 0);
    } catch (err) {
      setError(err.message || 'Failed to load members');
      setMembers([]);
      setTotal(0);
    } finally {
      setLoadingMembers(false);
    }
  }, [gymId, page, pageSize, query, rangeStart, rangeEnd]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadMembers();
    }, query.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadMembers, query]);

  useEffect(() => {
    setPage(1);
  }, [query, rangeStart, rangeEnd]);

  const handleExport = async (runExport) => {
    if (!gymId || exporting) return;
    try {
      setExporting(true);
      setActiveGymId(gymId);
      const result = await fetchMembersExport({
        search: query.trim(),
        filters: dateFilters,
      });
      runExport(dedupeMembers(result.rows || []));
    } catch (err) {
      const message = err.message || 'Export failed';
      setError(message);
      toast.showError(message);
    } finally {
      setExporting(false);
    }
  };

  // Search dropdown hits (server-filtered as user types)
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (!q || !gymId) {
      setMemberHits([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        setActiveGymId(gymId);
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (!cancelled) setMemberHits(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMemberHits([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, gymId]);

  const openMemberLedger = (member) => {
    if (!member?.id) return;
    navigate(`/admin/all-module/${member.id}`);
  };

  const handleMemberSelect = (next) => {
    if (next?.member?.id) {
      openMemberLedger(next.member);
      return;
    }
    if (next?.gymId) setQuery(next.gymId);
    else if (next?.name) setQuery(next.name);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="admin-all-module">
      <PageHeader
        title="Customer Ledger"
        subtitle="Search or pick a member card to open their full ledger"
      />

      <div className="owner-card rec-panel-card admin-all-module-search">
        <div className="admin-all-module-search-header">
          <MdPersonSearch className="admin-all-module-search-icon" aria-hidden />
          <div>
            <h2 className="owner-card-title">Member search</h2>
          </div>
        </div>

        <div className="admin-all-module-body">
          <GymIdMemberSearch
            id="all-module-member-search"
            query={query}
            onQueryChange={setQuery}
            selectedName=""
            selectedGymId=""
            onSelect={handleMemberSelect}
            members={memberHits}
            serverFiltered
            placeholder={MEMBER_SEARCH_PLACEHOLDER}
          />

          <PrintActionsHeader
            className="print-actions-header--inline admin-all-module-export"
            startDate={rangeStart}
            endDate={rangeEnd}
            onStartDateChange={setRangeStart}
            onEndDateChange={setRangeEnd}
            onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
            onPrint={() => handleExport(printMemberList)}
            onCsv={() => handleExport(exportMemberCsv)}
            onPdf={() => handleExport(exportMemberPdf)}
            onExcel={() => handleExport(exportMemberExcel)}
            onExport={() => handleExport(exportMembers)}
            ariaLabel="Customer ledger print and export"
          />

          {loadingMembers && (
            <p className="owner-placeholder-text" style={{ marginTop: 12 }}>Loading members…</p>
          )}
          {error && (
            <p className="owner-placeholder-text" style={{ marginTop: 12, color: 'var(--owner-danger, #dc2626)' }}>
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-customer-ledger-members">
        <div className="admin-all-module-section-header">
          <h2 className="owner-card-title">Members</h2>
          <span className="admin-all-module-count">{total}</span>
        </div>

        {!loadingMembers && members.length === 0 ? (
          <p className="owner-placeholder-text admin-all-module-hint">
            {query.trim() ? 'No members match your search.' : 'No members found for this gym.'}
          </p>
        ) : (
          <div className="admin-customer-ledger-grid">
            {members.map((member) => (
              <MemberCard
                key={member.id || getGymMemberId(member)}
                member={member}
                onClick={openMemberLedger}
              />
            ))}
          </div>
        )}

        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>
    </div>
  );
}
